import React from "react";
import PasswordInput from "../../../shared/components/imput/PasswordInput";
import { 
  TriangleAlert, 
  CheckCircle2, 
  LockKeyhole, 
  Check, 
  X, 
  ShieldCheck,
  AlertCircle,
  EyeOff
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { storageService } from "../service/storage";


export default function ChangePasswordForm({ 
  formData, 
  handleChange, 
  handleSubmit, 
  isLoading, 
  error, 
  success,
  shake ,
  requireOldPassword = false
}) {
  const {restartSetPass,setPass,directChangePassword} = useAuth();
  const { newPassword, confirmPassword ,oldPassword} = formData;
  
  // Validaciones
  const hasMinLength = newPassword.length >= 8;
  const hasUpperCase = /[A-Z]/.test(newPassword);
  const hasLowerCase = /[a-z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
  
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const passwordsMismatch = confirmPassword && newPassword !== confirmPassword;
  
  // Calcular fortaleza
  const strength = [hasMinLength, hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar]
    .filter(Boolean).length;

  const getStrengthLabel = () => {
    if (strength <= 2) return { text: 'Débil', class: 'weak' };
    if (strength <= 4) return { text: 'Media', class: 'medium' };
    return { text: 'Fuerte', class: 'strong' };
  };

  const strengthInfo = getStrengthLabel();

  const changePassword = () => {
    if(requireOldPassword ){
      directChangePassword({
        'oldPassword': oldPassword,
        'newPassword': newPassword
      });
    }
    else{
      const id = storageService.getSession();
      setPass({
        'sessionId': id,
        'password': newPassword
      })
    }
  }

  return (
    <>
      {/* Header con icono animado */}
      <div className="logo-wrapper-login">
        <div className="logo-container">
          <div className="security-icon-wrapper">
            {success ? (
              <CheckCircle2 size={48} className="security-icon success" />
            ) : (
              <ShieldCheck size={48} className="security-icon" />
            )}
          </div>
        </div>
      </div>

      <div className="brand-header">
        <h1>Actualizar <span className="highlight">Contraseña</span></h1>
        <p className="subtitle">Crea una contraseña segura para proteger tu cuenta</p>
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="error-message modern-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Mensaje de éxito */}
      {success && (
        <div className="success-message modern-success">
          <CheckCircle2 size={18} />
          <span>{success}</span>
        </div>
      )}

      <form 
        className={`login-form ${shake ? 'shake-animation' : ''}`} 
        onSubmit={handleSubmit}
      >
        {
          requireOldPassword && (
            <div className="input-field password-field-enhanced">
              <label htmlFor="oldPassword">Contraseña Actual</label>
              <PasswordInput
                formData={formData}
                handleChange={handleChange}
                name="oldPassword"
                id="oldPassword"
                placeholder="Ingresa tu contraseña actual"
                />
            </div>
          )
        }

        {/* Campo Nueva Contraseña */}
        <div className="input-field password-field-enhanced">
          <label htmlFor="newPassword">Nueva Contraseña</label>
          <PasswordInput 
            formData={formData} 
            handleChange={handleChange}
            name="newPassword"
            id="newPassword"
            placeholder="Mínimo 8 caracteres"
          />
          
          {/* Barra de fortaleza */}
          {newPassword && (
            <div className="strength-indicator">
              <div className="strength-bar">
                <div className={`strength-bar-fill ${strengthInfo.class}`} />
              </div>
              <span className={`strength-label ${strengthInfo.class}`}>
                Fortaleza: {strengthInfo.text}
              </span>
            </div>
          )}

          {/* Requisitos de contraseña */}
          {newPassword && (
            <div className="password-requirements">
              <RequirementItem 
                met={hasMinLength} 
                label="Mínimo 8 caracteres" 
              />
              <RequirementItem 
                met={hasUpperCase} 
                label="Mayúscula (A-Z)" 
              />
              <RequirementItem 
                met={hasLowerCase} 
                label="Minúscula (a-z)" 
              />
              <RequirementItem 
                met={hasNumber} 
                label="Número (0-9)" 
              />
              <RequirementItem 
                met={hasSpecialChar} 
                label="Carácter especial (!@#$...)" 
              />
            </div>
          )}
        </div>

        {/* Campo Confirmar Contraseña */}
        <div className="input-field password-field-enhanced">
          <label htmlFor="confirmPassword">Confirmar Contraseña</label>
          <PasswordInput 
            formData={formData} 
            handleChange={handleChange}
            name="confirmPassword"
            id="confirmPassword"
            placeholder="Repite tu contraseña"
          />
          
          {/* Indicador moderno de coincidencia */}
          {confirmPassword && (
            <div className={`match-indicator ${passwordsMatch ? 'valid' : 'invalid'}`}>
              {passwordsMatch ? (
                <>
                  <Check size={14} strokeWidth={3} />
                  <span>Las contraseñas coinciden perfectamente</span>
                </>
              ) : (
                <>
                  <X size={14} strokeWidth={3} />
                  <span>Las contraseñas no coinciden</span>
                </>
              )}
            </div>
          )}
          
          {/* Hint cuando aún no hay coincidencia pero sí hay input */}
          {confirmPassword && passwordsMismatch && (
            <div className="password-hint">
              <EyeOff size={12} />
              <span>Verifica que ambos campos sean idénticos</span>
            </div>
          )}
        </div>

        <button
          type="submit"
          className={`submit-btn ${isLoading ? "loading" : ""}`}
          disabled={isLoading || !passwordsMatch || strength < 3}
          onClick={() => {
            changePassword();
            restartSetPass(false);
          }}
        >
          {isLoading ? (
            <span className="loader" />
          ) : (
            <>
              <LockKeyhole size={18} style={{ marginRight: '8px' }} />
              Actualizar Contraseña
            </>
          )}
        </button>
      </form>

      <div className="form-footer">
            <button 
                type="button"
                className="text-link"
                onClick={() => {
                    restartSetPass(false);
                }}
                style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                padding: 0
                }}
            >
                ← Volver al inicio de sesión
            </button>
      </div>
    </>
  );
}

// Componente auxiliar para requisitos
function RequirementItem({ met, label }) {
  return (
    <div className={`requirement-item ${met ? 'met' : ''}`}>
      {met ? (
        <Check size={12} className="requirement-icon" />
      ) : (
        <div className="requirement-dot" />
      )}
      <span>{label}</span>
    </div>
  );
}